import { useSyncExternalStore } from 'react'

/*
 * Мобильный режим — это ширина, а не устройство: узкое окно на компьютере получает ту же
 * раскладку, что и телефон. Граница 767px — ниже неё три колонки не помещаются ни при
 * каком ужатии, и десктопная сетка превращается в то, что было на скриншотах: колонка
 * настроек в 200px и чат, обрезанный по правому краю.
 */
export const MOBILE_QUERY = '(max-width: 767px)'

const mq = typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(MOBILE_QUERY) : null

/** Синхронная проверка для кода вне React: сторы, обработчики. */
export function isMobileNow(): boolean {
  return mq?.matches ?? false
}

function subscribe(cb: () => void): () => void {
  if (!mq) return () => undefined
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}

/** Мобильная раскладка сейчас; обновляется при повороте и изменении окна. */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, isMobileNow, () => false)
}

/*
 * Правая колонка (участники, поиск, ветки) стоит рядом с чатом только от 1100 px. Уже чату
 * оставалось 250–450 px, и опрос с кнопками складывался в столбик; там колонка ложится
 * поверх чата (App.module.css). Меняете границу — меняйте и там.
 */
export const WIDE_QUERY = '(min-width: 1100px)'

const wideMq = typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(WIDE_QUERY) : null

/** Правой колонке хватает места рядом с чатом. Без matchMedia (тесты) — да, как раньше. */
export function isWideNow(): boolean {
  return wideMq?.matches ?? true
}

/** Окно стало шире или уже границы WIDE_QUERY. */
export function onWideChange(cb: (wide: boolean) => void): () => void {
  if (!wideMq) return () => undefined
  const onChange = (e: MediaQueryListEvent) => cb(e.matches)
  wideMq.addEventListener('change', onChange)
  return () => wideMq.removeEventListener('change', onChange)
}

/** Пальцем, а не мышью: подсказки при наведении недоступны, действия должны быть на виду. */
export const coarsePointer = typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia('(hover: none) and (pointer: coarse)').matches : false

/*
 * iPhone увеличивает страницу, когда нажимаешь на поле со шрифтом мельче 16 px (поиск эмодзи, поля
 * настроек), и так и оставляет: вёрстка уезжает за край, пока не сведёшь пальцами. maximum-scale=1
 * отключает только это автоувеличение — масштаб пальцами Safari с iOS 10 всё равно разрешает.
 * На Android автоувеличения нет, а maximum-scale там запретил бы и масштаб пальцами, поэтому только iOS.
 */
export function preventInputZoom(): void {
  if (typeof navigator === 'undefined') return
  const ua = navigator.userAgent
  // iPad с iPadOS 13+ представляется маком; отличает его только сенсорный экран
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  if (!ios) return
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (meta && !meta.content.includes('maximum-scale')) meta.content += ', maximum-scale=1'
}
